/**
 * Axepta UserData protocol: type-safe builders and parser for the pipe-delimited
 * UserData string that Axepta returns on payment redirects.
 *
 * Format:
 *   BOOKING:   "<affidiumPnr>|<sessionId>"
 *   DEPOSIT:   "deposit|<reference>|<amountCents>"
 *   INSURANCE: "insurance|<reference>|<bookingId>|<sessionId?>"
 */

const SEPARATOR = '|'

// ── Builders (server-side) ──

export type ParsedPaymentConfirmationUserData =
  | { amountCents?: number; bookingRef?: string; flow: 'deposit'; }
  | { bookingId?: string; bookingRef?: string; flow: 'insurance'; sessionId?: string }
  | { bookingRef?: string; flow: 'booking'; sessionId?: string }
  | { flow: 'unknown' }

export type PaymentConfirmationFlow = 'booking' | 'deposit' | 'insurance' | 'unknown'

export function buildBookingUserData(pnr: string, sessionId: string): string {
  return [pnr, sessionId].join(SEPARATOR)
}

// ── Parser (frontend) ──

export function buildDepositUserData(reference: string, amountCents: number): string {
  return ['deposit', reference, String(amountCents)].join(SEPARATOR)
}

export function buildInsuranceUserData(
  reference: string,
  bookingId: string,
  sessionId?: string
): string {
  const parts = ['insurance', reference, bookingId]
  if (sessionId) parts.push(sessionId)
  return parts.join(SEPARATOR)
}

export function parsePaymentConfirmationUserData(
  userData: string | undefined
): ParsedPaymentConfirmationUserData {
  if (!userData) {
    return { flow: 'unknown' }
  }

  const parts = userData.split(SEPARATOR)
  const [first, second, third, fourth] = parts

  // DEPOSIT: "deposit|<reference>|<amountCents>"
  if (first === 'deposit' || first === 'DEPOSIT') {
    return {
      amountCents: parseAmountCents(third),
      bookingRef: second || undefined,
      flow: 'deposit',
    }
  }

  // INSURANCE: "insurance|<reference>|<bookingId>|<sessionId?>"
  if (first === 'insurance' || first === 'INSURANCE') {
    return {
      bookingId: third || undefined,
      sessionId: fourth || undefined,
      bookingRef: second || undefined,
      flow: 'insurance',
    }
  }

  // BOOKING (checkout): "<affidiumPnr>|<sessionId>"
  return {
    sessionId: second || undefined,
    bookingRef: first || undefined,
    flow: 'booking',
  }
}

function parseAmountCents(value: string | undefined): number | undefined {
  if (!value) return undefined
  const n = Number(value)
  return Number.isFinite(n) ? n : undefined
}
